"use client";
import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useReactToPrint } from "react-to-print";

// ✅ Preview components
import AppreciationLetter from "../letters/AppreciationLetter";
import AppraisalLetter from "../letters/AppraisalLetter";
import DemotionLetter from "../letters/DemotionLetter";
import FullFinalSettlementLetter from "../letters/FullFinalSettlementLetter";
import PromotionLetter from "../letters/PromotionLetter";
import TerminationLetter from "../letters/TerminationLetter";
import WarningLetter from "./RelievingLetter";
import MisconductLetter from "../letters/MisconductLetter";
import LowPerformanceWarningLetter from "./LowPerformanceWarningLetter";
import DeviceAllocationLetter from "./DeviceAllocationLetter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Input } from "src/components/input/input";
import { Button } from "src/components/buttons/shadcn-button";

// ✅ Letter types
const letterTypes = [
  "Appreciation Letter",
  "Appraisal Letter",
  "Demotion Letter",
  "Full and Final Settlement Letter",
  "Promotion Letter",
  "Termination Letter",
  "Low Performance Warning Letter",
  "Relieving Letter",
  "Warning Letter for Misconduct",
  "Device Allocation Letter"
  
];

// ✅ Fields map
const fieldsMap: { [key: string]: string[] } = {
  "Appreciation Letter": ["refNo", "date", "name", "address"],
  "Appraisal Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "period",
    "revisedSalary",
    "effectiveDate",
  ],
  "Demotion Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "Demoted_Role",
    "Role",
    "effectiveDate",
  ],
  "Full and Final Settlement Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "amount",
    "bank_number",
  ],
  "Promotion Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "Promoted_Role",
    "Role",
    "effectiveDate",
    "duties",
  ],
  "Low Performance Warning Letter": [
    "refNo", "date", "name", "address"
  ],
  "Termination Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "designation",
    "lastDate",
    "termination_reason",
    "accessories",
  ],
  "Relieving Letter": ["refNo", "date", "name", "address", "lastDate"],
  "Warning Letter for Misconduct": [
    "refNo",
    "date",
    "name",
    "address",
    "incidentDate",
    "incident",
  ],
  "Device Allocation Letter": [
    "refNo",
    "date",
    "name",
    "address",
    "emp_id",
    "device_id",
  ]
};

function LetterSelector() {
  const form = useForm({ defaultValues: { letterType: "" } });
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [letterData, setLetterData] = useState<any>({
    refNo: "[refno]",
    date: "[date]",
    name: "[name]",
    address: "Guwahati, Assam",
    period: "[period]",
    revisedSalary: "[revisedSalary]",
    effectiveDate: "[effectiveDate]",
    Demoted_Role: "[Demoted_Role]",
    amount: "[amount]",
    lastDate: "[lastDate]",
    incidentDate: "[incidentDate]",
    incident: "[incident]",
    Role: "[Role]",
    Promoted_Role: "[Promoted_Role]",
    bank_number: "[bank_number]",
    designation: "[designation]",
    termination_reason: "[termination_reason]",
    accessories: "[accessories]",
    duties: "[duties]",
    device_id: "[device_id]",
    emp_id: "[emp_id]",

  });

  const handleCreateLetter = () => {
    const value = form.getValues("letterType");
    if (!value) {
      alert("Please select a letter type first!");
      return;
    }
    setSelectedValue(value);
    setIsEditing(false);
  };

  // 👇 Ref for printing
  const componentRef = useRef<HTMLDivElement>(null);

  // 👇 Print handler for react-to-print@3
  const handlePrint = useReactToPrint({
    documentTitle: selectedValue
      ? `${selectedValue.replace(/\s+/g, "_")}_Letter`
      : "Letter",

      pageStyle: `

      @page {
          size: A4;
          margin: 0;
      }
      
      body {
      zoom: 0.95;
      }
      `,
})
      
      
      
      

  // ✅ Render the selected letter preview
  const renderLetterPreview = () => {
    switch (selectedValue) {
      case "Appreciation Letter":
        return <AppreciationLetter {...letterData}/>;
      case "Appraisal Letter":
        return <AppraisalLetter {...letterData}/>;
      case "Demotion Letter":
        return <DemotionLetter {...letterData}/>;
      case "Full and Final Settlement Letter":
        return <FullFinalSettlementLetter {...letterData}/>;
      case "Promotion Letter":
        return <PromotionLetter {...letterData}/>;
      case "Termination Letter":
        return <TerminationLetter {...letterData}/>;
      case "Relieving Letter":
        return <WarningLetter {...letterData}/>;
      case "Low Performance Warning Letter":
        return <LowPerformanceWarningLetter {...letterData}/>  
      case "Warning Letter for Misconduct":
        return <MisconductLetter {...letterData}/>;
        case "Device Allocation Letter":
          return <DeviceAllocationLetter {...letterData}/>
      default:
        return null;
    }
  };

  // ✅ Render edit form for letter fields
  const renderEditForm = () => {
    if (!selectedValue) return null;
    const fields = fieldsMap[selectedValue] || [];

    return (
      <div className="space-y-4 mt-4">
        {fields.map((key) => (
          <div key={key} className="flex flex-col">
            <label className="text-sm font-medium capitalize">{key}</label>
            <Input
              value={letterData[key] || ""}
              onChange={(e) =>
                setLetterData({ ...letterData, [key]: e.target.value })
              }
            />
          </div>
        ))}
        <div className="flex gap-2 mt-4">
          <Button onClick={() => setIsEditing(false)}>Save</Button>
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Form {...form}>
        <FormField
          control={form.control}
          name="letterType"
          render={({ field }) => (
            <FormItem className="w-full">
              <div className="flex items-center justify-between">
                <FormLabel>Select Letter Type</FormLabel>
                <Button type="button" onClick={handleCreateLetter}>
                  Create Letter
                </Button>
              </div>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a letter type" />
                  </SelectTrigger>
                  <SelectContent>
                    {letterTypes.map((letter) => (
                      <SelectItem key={letter} value={letter}>
                        {letter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Form>

      <div className="mt-6">
        {selectedValue && !isEditing && (
          <>
            {/* 👇 Print-ready letter container (no border/shadow) */}
            <div ref={componentRef}>{renderLetterPreview()}</div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                Edit Letter
              </Button>

              {/* 👇 Print/export button */}
              <Button onClick={() => handlePrint(() => componentRef.current!)}>
                Download / Print Letter
              </Button>
            </div>
          </>
        )}
        {isEditing && renderEditForm()}
      </div>
    </>
  );
}

export default LetterSelector;
